/**
 * Pricing Page - Reconflux
 *
 * Public pricing page showcasing subscription tiers.
 * Brand-aligned with "The Value of Time" messaging.
 */

import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Logo } from '../components/ui/Logo'
import { useState } from 'react'
import { useAuth } from '../providers/AuthProvider'
import { useOrganization } from '../providers/OrganizationProvider'
import { supabase } from '../lib/supabase'
import { PLAN_TIERS } from '../config/plans'
import { TIER_PRESENTATION } from '../config/tierCopy'
import { ENTERPRISE_EMAIL, PRIVACY_URL, SUPPORT_URL, TERMS_URL, marketingHomeUrl } from '../lib/marketingSite'

// Marketing copy is presentation-only and lives in `config/tierCopy` (key
// parity with the catalog is test-guarded there); the structural plan data
// (name, price, period, description) comes from the shared source of truth in
// `shared/plans.json` via `config/plans`.
const TIERS = PLAN_TIERS.map((plan) => ({
  id: plan.id,
  name: plan.name,
  price: plan.display_price,
  period: plan.period,
  description: plan.description,
  ...TIER_PRESENTATION[plan.id],
}))

export function PricingPage() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const { currentOrganization, canManageOrganization } = useOrganization()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleSelectTier = async (tierId: string) => {
    // If not logged in, redirect to signup
    if (!user) {
      navigate('/signup')
      return
    }

    // If no organization, redirect to onboarding
    if (!currentOrganization) {
      navigate('/onboarding')
      return
    }

    // For enterprise, open email
    if (tierId === 'enterprise') {
      window.location.href = `mailto:${ENTERPRISE_EMAIL}?subject=${encodeURIComponent('Clara Enterprise Inquiry')}`
      return
    }

    // For trial, they already have it
    if (tierId === 'trial') {
      navigate('/dashboard')
      return
    }

    // For paid tiers, initiate checkout
    if (!canManageOrganization) {
      setError(t('notOwner', { ns: 'billing' }))
      return
    }

    setIsLoading(tierId)
    setError(null)

    try {
      const { data: session } = await supabase.auth.getSession()
      if (!session.session) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.session.access_token}`,
          },
          body: JSON.stringify({
            organizationId: currentOrganization.id,
            tier: tierId,
            successUrl: `${window.location.origin}/settings?billing=success`,
            cancelUrl: `${window.location.origin}/pricing`,
          }),
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session')
      }

      window.location.href = data.url
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout')
      setIsLoading(null)
    }
  }

  return (
    <div className="min-h-dvh bg-surface-page">
      {/* Header */}
      <header className="border-b border-edge/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Logo href={marketingHomeUrl(i18n.resolvedLanguage)} />

            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    className="text-sm text-ink-subtle hover:text-ink transition-colors"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/settings?tab=billing"
                    className="text-sm text-accent hover:text-accent-muted transition-colors"
                  >
                    Manage Billing
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="inline-flex items-center min-h-11 md:min-h-0 text-sm text-ink-subtle hover:text-ink transition-colors"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="inline-flex items-center justify-center min-h-11 px-4 py-2 bg-accent text-accent-contrast text-sm font-medium rounded-lg
                      hover:bg-accent-hover transition-colors"
                  >
                    Start Free Trial
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-ink mb-6">
            The Value of <span className="text-accent">Time</span>
          </h1>
          <p className="text-xl text-ink-subtle max-w-2xl mx-auto">
            Clara does not charge by the line. What comes back is the time you
            would have spent on data entry. Choose the plan that fits your volume.
          </p>
        </div>
      </section>

      {/* Error message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="bg-danger/10 border border-danger/30 rounded-lg px-4 py-3">
            <p className="text-danger text-sm text-center">{error}</p>
          </div>
        </div>
      )}

      {/* Pricing Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {TIERS.map((tier) => (
              <div
                key={tier.id}
                className={`relative bg-surface-raised rounded-xl p-6 flex flex-col ${
                  tier.highlight
                    ? 'border-2 border-accent ring-1 ring-accent/20'
                    : 'border border-edge/50'
                }`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-accent text-accent-contrast text-xs font-bold rounded-full uppercase tracking-wider">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-ink mb-1">
                    {tier.name}
                  </h3>
                  <p className="text-sm text-ink-subtle">{tier.description}</p>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-accent">{tier.price}</span>
                  {tier.period && (
                    <span className="text-ink-muted ml-1">{tier.period}</span>
                  )}
                </div>

                <p className="text-sm text-ink-subtle mb-6 italic">
                  "{tier.tagline}"
                </p>

                <ul className="space-y-3 mb-8 flex-grow">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <CheckIcon className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-ink-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectTier(tier.id)}
                  disabled={isLoading === tier.id}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors
                    disabled:opacity-50 disabled:cursor-not-allowed ${
                      tier.ctaVariant === 'primary'
                        ? 'bg-accent text-accent-contrast hover:bg-accent-hover'
                        : 'bg-surface-page border border-edge/50 text-ink hover:border-accent/50'
                    }`}
                >
                  {isLoading === tier.id ? 'Processing...' : tier.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ or Trust signals */}
      <section className="py-16 border-t border-edge/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-semibold text-ink mb-4">
            The 21-Day Ritual
          </h2>
          <p className="text-ink-subtle max-w-2xl mx-auto">
            We offer a 21-day trial because that's the scientific window for habit formation.
            We want you to habituate to a world where your spending is always visible,
            where invoices process themselves, and where clarity is the default.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-edge/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink-muted">
              Clara - Reclaim your focus. Own your future.
            </p>
            <div className="flex items-center gap-6">
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center min-h-11 md:min-h-0 text-sm text-ink-muted hover:text-ink transition-colors"
              >
                Privacy
              </a>
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center min-h-11 md:min-h-0 text-sm text-ink-muted hover:text-ink transition-colors"
              >
                Terms
              </a>
              <a
                href={SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center min-h-11 md:min-h-0 text-sm text-ink-muted hover:text-ink transition-colors"
              >
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  )
}
